// Customer icon picker for the blank ("vierge") cover.
//
// A Modal grid of the covers catalogue's transparent décor PNGs, served as
// light Supabase-transform thumbnails (iconThumbUrl — tens of KB, not the
// 1.7MB print PNGs). Icons matching the customer's typed destination float
// first (sortIconsByDestination — the same tolerant scorer as the cover
// picker's auto-match). Picking a tile hands the full-res public design_url
// back to the editor, which renders it through the EXISTING front-cover
// override chain (CoverThumbnail frontImageSrcOverride + the PDF's
// frontCoverImageOverrideUrl) — contain-fit, drag/zoom via
// coverDesignTransform and PDF parity all come for free.
import { useEffect, useMemo, useState } from 'react';
import type { Cover } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { iconSrc, normDest, sortIconsByDestination } from '@/lib/coverIcons';

// Brand tokens (mirrors the editor's COLORS palette).
const GOLD = '#C9A24A';
const CREAM = '#FAF6EC';
const DARK = '#1F2937';
const MUTED = '#6B7280';

export function CoverIconPickerModal({
  isOpen,
  onClose,
  covers,
  destination,
  onPick,
}: {
  isOpen: boolean;
  onClose: () => void;
  covers: Cover[];
  destination: string;
  onPick: (cover: Cover) => void;
}) {
  const [search, setSearch] = useState('');
  // Fresh search on every open so a stale filter never hides the grid.
  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const { list, matchCount } = useMemo(
    () => sortIconsByDestination(covers, destination),
    [covers, destination],
  );

  const q = normDest(search);
  const filtered = useMemo(
    () =>
      q
        ? list.filter(
            (c) =>
              normDest(c.name).includes(q) ||
              (c.search_tags ?? []).some((t) => normDest(t).includes(q)),
          )
        : list,
    [list, q],
  );

  // The "for your destination" section only makes sense on the unfiltered,
  // destination-sorted list — an active search reorders/filters freely.
  const showSections = !q && matchCount > 0;
  const matched = showSections ? filtered.slice(0, matchCount) : [];
  const rest = showSections ? filtered.slice(matchCount) : filtered;

  const sectionLabel = (text: string) => (
    <p
      className="col-span-full text-[10px] font-semibold uppercase tracking-widest mt-1"
      style={{ color: GOLD }}
    >
      {text}
    </p>
  );

  const renderTile = (c: Cover) => (
    <button
      key={c.id}
      type="button"
      onClick={() => onPick(c)}
      className="rounded-xl overflow-hidden text-left transition-transform hover:scale-[1.03] active:scale-95"
      style={{ border: `1px solid ${GOLD}25`, background: '#FFFFFF' }}
      title={c.name}
    >
      <div
        className="w-full aspect-square flex items-center justify-center p-2"
        style={{ background: CREAM }}
      >
        <img
          loading="lazy"
          decoding="async"
          src={iconSrc(c, 200)}
          alt={c.name}
          className="max-w-full max-h-full object-contain"
          draggable={false}
        />
      </div>
      <p className="px-1.5 py-1 text-[10px] truncate" style={{ color: DARK }}>
        {c.name}
      </p>
    </button>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choisir une icône" size="lg">
      <div className="p-4 flex flex-col gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher une icône…"
          className="w-full rounded-full px-4 py-2 text-sm focus:outline-none"
          style={{
            border: `1px solid ${GOLD}40`,
            background: CREAM,
            color: DARK,
          }}
        />
        <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
          {showSections && sectionLabel(`✦ Pour « ${destination} »`)}
          {matched.map(renderTile)}
          {showSections && sectionLabel('Toutes les icônes')}
          {rest.map(renderTile)}
          {filtered.length === 0 && (
            <p className="col-span-full text-center text-xs py-8" style={{ color: MUTED }}>
              Aucune icône trouvée
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
